/**
 * K108-R Batch 3 — completion-modal options tailored to monitored engagement.
 *
 * Pure + tested. Given OptiBay's observed `monitoredPercent`, produce the
 * user-response options and a NEUTRAL attestation snapshot (stored in the
 * session's `metadata`, no schema change). Language is honest: OptiBay
 * "observed" engagement evidence; the user "reported" — never "verified
 * completion", never accusatory.
 */

export type MonitoredBucket = '0-20' | '20-80' | '80-100' | 'unavailable';

/** User's reported outcome — a superset of the DB completion enum plus the neutral dispute value. */
export type UserReported =
  | 'completed'
  | 'partial'
  | 'abandoned'
  | 'skipped'
  | 'monitoring_incorrect';

/** DB completion_status — kept within the EXISTING enum (no needs_review etc.). */
export type ConfirmStatus = 'completed' | 'partial' | 'skipped';

export interface CompletionOptionDef {
  id: string;
  label: string;
  userReported: UserReported;
  /** 'confirm' → write completion_status; 'remind' → postpone (no status change). */
  action: 'confirm' | 'remind';
  confirmStatus?: ConfirmStatus;
  progressPercent?: number;
}

const OPT = {
  completed: { id: 'completed', label: 'Yes, completed in full', userReported: 'completed', action: 'confirm', confirmStatus: 'completed' },
  mostly: { id: 'mostly', label: 'Mostly completed', userReported: 'partial', action: 'confirm', confirmStatus: 'partial', progressPercent: 85 },
  partial: { id: 'partial', label: 'Partially completed', userReported: 'partial', action: 'confirm', confirmStatus: 'partial', progressPercent: 50 },
  partialLow: { id: 'partial_low', label: 'I partially completed it', userReported: 'partial', action: 'confirm', confirmStatus: 'partial', progressPercent: 50 },
  notYet: { id: 'not_yet', label: 'I did not get to it yet', userReported: 'abandoned', action: 'remind' },
  completedUncaptured: { id: 'completed_uncaptured', label: 'I completed it, but OptiBay did not capture it correctly', userReported: 'completed', action: 'confirm', confirmStatus: 'completed' },
  monitoringWrong: { id: 'monitoring_wrong', label: "OptiBay's monitored progress looks wrong", userReported: 'monitoring_incorrect', action: 'confirm', confirmStatus: 'partial' },
  skip: { id: 'skip', label: 'Not relevant / skip', userReported: 'skipped', action: 'confirm', confirmStatus: 'skipped' },
} satisfies Record<string, CompletionOptionDef>;

/** [0,20) → 0-20 · [20,80) → 20-80 · [80,100] → 80-100 · null → unavailable. */
export function monitoredBucket(monitoredPercent: number | null): MonitoredBucket {
  if (monitoredPercent == null) return 'unavailable';
  if (monitoredPercent < 20) return '0-20';
  if (monitoredPercent < 80) return '20-80';
  return '80-100';
}

export function completionOptionsForBucket(bucket: MonitoredBucket): CompletionOptionDef[] {
  switch (bucket) {
    case '0-20':
      return [OPT.notYet, OPT.partialLow, OPT.completedUncaptured, OPT.skip];
    case '20-80':
      return [OPT.partial, OPT.completed, OPT.monitoringWrong, OPT.skip];
    case '80-100':
      return [OPT.completed, OPT.mostly, OPT.monitoringWrong, OPT.skip];
    case 'unavailable':
    default:
      return [OPT.completed, OPT.partial, OPT.notYet, OPT.skip];
  }
}

/**
 * Neutral mismatch derivation — NEVER accusatory. A mismatch is recorded when:
 *  - the user explicitly disputes the monitoring, OR
 *  - OptiBay observed LOW engagement but the user reports completed, OR
 *  - OptiBay observed HIGH engagement but the user reports not-completed (abandoned).
 * Returns 'user_attestation_mismatch' or null.
 */
export function deriveAttestationMismatch(
  userReported: UserReported,
  monitoredPercent: number | null,
): 'user_attestation_mismatch' | null {
  if (userReported === 'monitoring_incorrect') return 'user_attestation_mismatch';
  if (monitoredPercent == null) return null;
  if (monitoredPercent < 20 && userReported === 'completed') return 'user_attestation_mismatch';
  if (monitoredPercent >= 80 && userReported === 'abandoned') return 'user_attestation_mismatch';
  return null;
}

export interface TrainingAttestation {
  userReported: UserReported;
  monitoredPercent: number | null;
  confidence: 'low' | 'medium' | null;
  evidenceUnavailable: boolean;
  mismatch: 'user_attestation_mismatch' | null;
  at: string;
}

export function buildTrainingAttestation(args: {
  userReported: UserReported;
  monitoredPercent: number | null;
  confidence: 'low' | 'medium' | null;
  evidenceUnavailable: boolean;
  at: string;
}): TrainingAttestation {
  return {
    userReported: args.userReported,
    monitoredPercent: args.monitoredPercent,
    confidence: args.confidence,
    evidenceUnavailable: args.evidenceUnavailable,
    mismatch: deriveAttestationMismatch(args.userReported, args.monitoredPercent),
    at: args.at,
  };
}

/**
 * Honest observed-engagement line for the modal. Never "verified completion".
 * Uses the corrected engagement percent (active/duration) when available; falls
 * back to "X minutes of engagement" when there is no trustworthy duration — never
 * a fabricated percent.
 */
export function observedEngagementCopy(
  monitoredPercent: number | null,
  evidenceUnavailable: boolean,
  observedMinutes?: number | null,
): string {
  if (evidenceUnavailable) {
    return 'OptiBay does not have monitored engagement evidence for this training artifact yet.';
  }
  if (monitoredPercent != null) {
    return `OptiBay observed approximately ${Math.round(monitoredPercent)}% engagement with this training artifact.`;
  }
  if (observedMinutes != null && observedMinutes > 0) {
    return `OptiBay observed approximately ${observedMinutes} minute${observedMinutes === 1 ? '' : 's'} of engagement with this training artifact.`;
  }
  return 'OptiBay does not have monitored engagement evidence for this training artifact yet.';
}

/** Neutral post-submit note when observed engagement differs from the user's report. */
export function mismatchNoteCopy(monitoredPercent: number | null): string {
  if (monitoredPercent != null && monitoredPercent < 20) {
    return 'OptiBay observed limited engagement. Your report has been saved.';
  }
  return 'Your report has been saved.';
}
